// Web-compatible imports - only load Capacitor plugins in mobile environment
let Preferences, Device;

// Check if we're in a mobile environment
const isMobile = typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform();

if (isMobile) {
  // Only import Capacitor plugins on mobile
  try {
    Preferences = require('@capacitor/preferences').Preferences;
    Device = require('@capacitor/device').Device;
  } catch (error) {
    console.warn('Capacitor plugins not available, falling back to web storage');
  }
}

class CapacitorStorageService {
  constructor() {
    this.isMobile = false;
    this.storagePrefix = 'acctoo_';
  }

  // Initialize storage service
  async initialize() {
    try {
      if (isMobile && Device) {
        const info = await Device.getInfo();
        this.isMobile = info.platform === 'ios' || info.platform === 'android';
      } else {
        // Web environment - use localStorage
        this.isMobile = false;
        console.log('🌐 Web environment detected, using localStorage');
      }
    } catch (error) {
      console.error('Error initializing CapacitorStorage:', error);
      this.isMobile = false;
    }
  }

  // Set item in storage
  async setItem(key, value) {
    try {
      const storageKey = `${this.storagePrefix}${key}`;
      
      if (this.isMobile && Preferences) {
        // Use Capacitor Preferences for mobile
        await Preferences.set({
          key: storageKey,
          value: typeof value === 'string' ? value : JSON.stringify(value)
        });
      } else {
        // Use localStorage for web
        localStorage.setItem(storageKey, typeof value === 'string' ? value : JSON.stringify(value));
      }
      
      return true;
    } catch (error) {
      console.error('Error setting item in storage:', error);
      return false;
    }
  }

  // Get item from storage
  async getItem(key, defaultValue = null) {
    try {
      const storageKey = `${this.storagePrefix}${key}`;
      let value = null;
      
      if (this.isMobile && Preferences) {
        // Use Capacitor Preferences for mobile
        const result = await Preferences.get({ key: storageKey });
        value = result.value;
      } else {
        // Use localStorage for web
        value = localStorage.getItem(storageKey);
      }
      
      if (value === null || value === undefined) {
        return defaultValue;
      }
      
      // Try to parse as JSON, fallback to string
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    } catch (error) {
      console.error('Error getting item from storage:', error);
      return defaultValue;
    }
  }

  // Remove item from storage
  async removeItem(key) {
    try {
      const storageKey = `${this.storagePrefix}${key}`;
      
      if (this.isMobile && Preferences) {
        // Use Capacitor Preferences for mobile
        await Preferences.remove({ key: storageKey });
      } else {
        // Use localStorage for web
        localStorage.removeItem(storageKey);
      }
      
      return true;
    } catch (error) {
      console.error('Error removing item from storage:', error);
      return false;
    }
  }

  // Clear all storage
  async clear() {
    try {
      if (this.isMobile && Preferences) {
        // Get all keys and remove them
        const keys = await Preferences.keys();
        for (const key of keys.keys) {
          if (key.startsWith(this.storagePrefix)) {
            await Preferences.remove({ key });
          }
        }
      } else {
        // Clear localStorage
        const keys = Object.keys(localStorage);
        for (const key of keys) {
          if (key.startsWith(this.storagePrefix)) {
            localStorage.removeItem(key);
          }
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error clearing storage:', error);
      return false;
    }
  }

  // Check if key exists
  async hasItem(key) {
    try {
      const value = await this.getItem(key);
      return value !== null && value !== undefined;
    } catch (error) {
      console.error('Error checking item in storage:', error);
      return false;
    }
  }

  // Get all keys
  async getAllKeys() {
    try {
      if (this.isMobile && Preferences) {
        const keys = await Preferences.keys();
        return keys.keys.filter(key => key.startsWith(this.storagePrefix));
      } else {
        const keys = Object.keys(localStorage);
        return keys.filter(key => key.startsWith(this.storagePrefix));
      }
    } catch (error) {
      console.error('Error getting all keys:', error);
      return [];
    }
  }

  // Get storage size
  async getStorageSize() {
    try {
      const keys = await this.getAllKeys();
      let totalSize = 0;
      
      for (const key of keys) {
        const value = await this.getItem(key);
        totalSize += JSON.stringify(value).length;
      }
      
      return {
        keys: keys.length,
        size: totalSize,
        sizeFormatted: this.formatBytes(totalSize)
      };
    } catch (error) {
      console.error('Error getting storage size:', error);
      return { keys: 0, size: 0, sizeFormatted: '0 B' };
    }
  }

  // Format bytes to human readable
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  // Login persistence methods
  async saveLoginData(userData) {
    try {
      const loginData = {
        user: userData,
        timestamp: Date.now(),
        deviceInfo: await this.getDeviceInfo()
      };
      
      await this.setItem('login_data', loginData);
      await this.setItem('is_logged_in', true);
      
      return true;
    } catch (error) {
      console.error('Error saving login data:', error);
      return false;
    }
  }

  async getLoginData() {
    try {
      const loginData = await this.getItem('login_data');
      const isLoggedIn = await this.getItem('is_logged_in', false);
      
      if (!loginData || !isLoggedIn) {
        return null;
      }
      
      // Check if login data is not too old (30 days)
      const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days
      if (Date.now() - loginData.timestamp > maxAge) {
        await this.clearLoginData();
        return null;
      }
      
      return loginData;
    } catch (error) {
      console.error('Error getting login data:', error);
      return null;
    }
  }

  async clearLoginData() {
    try {
      await this.removeItem('login_data');
      await this.removeItem('is_logged_in');
      return true;
    } catch (error) {
      console.error('Error clearing login data:', error);
      return false;
    }
  }

  async getDeviceInfo() {
    try {
      if (isMobile && Device) {
        const info = await Device.getInfo();
        return {
          platform: info.platform,
          model: info.model,
          osVersion: info.osVersion,
          appVersion: info.appVersion,
          isVirtual: info.isVirtual
        };
      } else {
        // Web environment - return browser info
        return {
          platform: 'web',
          model: navigator.userAgent,
          osVersion: navigator.platform,
          appVersion: '1.0.0',
          isVirtual: false
        };
      }
    } catch (error) {
      console.error('Error getting device info:', error);
      return {
        platform: 'web',
        model: 'browser',
        osVersion: 'unknown',
        appVersion: '1.0.0',
        isVirtual: false
      };
    }
  }

  // Session management
  async saveSession(sessionData) {
    try {
      const session = {
        ...sessionData,
        timestamp: Date.now(),
        expiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000) // 30 days
      };
      
      await this.setItem('session', session);
      return true;
    } catch (error) {
      console.error('Error saving session:', error);
      return false;
    }
  }

  async getSession() {
    try {
      const session = await this.getItem('session');
      
      if (!session) {
        return null;
      }
      
      // Check if session is expired
      if (Date.now() > session.expiresAt) {
        await this.removeItem('session');
        return null;
      }
      
      return session;
    } catch (error) {
      console.error('Error getting session:', error);
      return null;
    }
  }

  async clearSession() {
    try {
      await this.removeItem('session');
      return true;
    } catch (error) {
      console.error('Error clearing session:', error);
      return false;
    }
  }
}

export default new CapacitorStorageService();
